import { geoDistance, geoInterpolate } from 'd3-geo';

/**
 * The live-round intro as a pure function of time: given t (ms since the intro started, from the
 * server-synchronised clock) it returns what the globe and overlays show. No setTimeout chains — the
 * renderer asks for the frame at "now", so a late joiner or a slow device lands on the right frame.
 */
export interface IntroTiming {
  rotateStart: number;
  countryVisible: number;
  markerAt: number;
  infoAt: number;
  zoomAccelAt: number;
  transitionAt: number;
  videoAt: number;
  playingAt: number;
}

/** Defaults from the brief (T+0.5 … T+7.5 s). */
export const DEFAULT_INTRO: IntroTiming = { rotateStart: 500, countryVisible: 2000, markerAt: 3000, infoAt: 3500, zoomAccelAt: 5000, transitionAt: 6500, videoAt: 7000, playingAt: 7500 };

export interface LonLat {
  lon: number;
  lat: number;
}

export type IntroPhase = 'EARTH' | 'ROTATING' | 'COUNTRY' | 'MARKER' | 'ZOOM' | 'TRANSITION' | 'VIDEO' | 'PLAYING';

export interface IntroFrame {
  phase: IntroPhase;
  /** Globe rotation centre (the point facing the viewer). */
  center: LonLat;
  /** Zoom factor relative to the whole-Earth view (1 = whole globe). */
  zoom: number;
  /** Highlight of the camera's country (appears when the country becomes visible). */
  countryOpacity: number;
  markerOpacity: number;
  infoOpacity: number;
  /** 0 = only the globe, 1 = only the video. */
  videoOpacity: number;
  /** A short white-out flash at the hand-off (0..1). */
  flash: number;
}

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const ease = (x: number) => (x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2);
const span = (t: number, a: number, b: number) => (b > a ? clamp01((t - a) / (b - a)) : t >= b ? 1 : 0);

/** Zoom levels: Earth → continent → country → city. The final stage is a cinematic cut, not a street zoom. */
export const ZOOM = { earth: 1, continent: 2.6, country: 7, city: 28 };

export function introFrame(t: number, from: LonLat, to: LonLat, timing: IntroTiming = DEFAULT_INTRO): IntroFrame {
  // Rotation: great-circle path from the previous view to the camera, eased, done by the time the marker appears.
  const rot = ease(span(t, timing.rotateStart, timing.markerAt));
  const [lon, lat] = geoInterpolate([from.lon, from.lat], [to.lon, to.lat])(rot);
  // Zoom in log space: slow to country level, then accelerating towards the city until the hand-off.
  const lg = (z: number) => Math.log(z);
  const z1 = span(t, timing.rotateStart, timing.zoomAccelAt);
  const z2 = span(t, timing.zoomAccelAt, timing.videoAt);
  const logZoom = lg(ZOOM.earth) + (lg(ZOOM.country) - lg(ZOOM.earth)) * ease(z1) + (lg(ZOOM.city) - lg(ZOOM.country)) * z2 * z2;
  const video = span(t, timing.transitionAt, timing.videoAt);
  const flash = t >= timing.transitionAt && t <= timing.videoAt ? Math.sin(Math.PI * span(t, timing.transitionAt, timing.videoAt)) * 0.3 : 0;
  const phase: IntroPhase =
    t >= timing.playingAt
      ? 'PLAYING'
      : t >= timing.videoAt
        ? 'VIDEO'
        : t >= timing.transitionAt
          ? 'TRANSITION'
          : t >= timing.zoomAccelAt
            ? 'ZOOM'
            : t >= timing.markerAt
              ? 'MARKER'
              : t >= timing.countryVisible
                ? 'COUNTRY'
                : t >= timing.rotateStart
                  ? 'ROTATING'
                  : 'EARTH';
  return {
    phase,
    center: { lon: lon!, lat: lat! },
    zoom: Math.exp(logZoom),
    countryOpacity: span(t, timing.countryVisible, timing.countryVisible + 500),
    markerOpacity: span(t, timing.markerAt, timing.markerAt + 400) * (1 - video),
    infoOpacity: span(t, timing.infoAt, timing.infoAt + 400) * (1 - span(t, timing.videoAt, timing.playingAt)),
    videoOpacity: video,
    flash,
  };
}

/** Angular distance in degrees (for choosing a sensible start view). */
export const degreesBetween = (a: LonLat, b: LonLat) => (geoDistance([a.lon, a.lat], [b.lon, b.lat]) * 180) / Math.PI;

/** Only fields we actually hold, upper-cased for the title card; never invented. */
export function locationLines(cam: { city: string | null; country: string | null; road: string | null; name: string | null }): { title: string[]; detail: string[] } {
  const title = [cam.city, cam.country].filter((x): x is string => !!x && x.trim() !== '').map((x) => x.toUpperCase());
  const detail = [cam.road, cam.name && cam.name !== cam.road ? cam.name : null].filter((x): x is string => !!x && x.trim() !== '');
  return { title, detail };
}
